/**
 * 문전약국 목록 화면.
 *
 * admin-ruda-web `pharmacyInfo.js` 포팅.
 * - 약국명·관련 병의원 검색 + 영업상태 필터 + 서버 페이지네이션
 * - 엑셀은 현재 검색 조건 그대로 서버가 만들어 서명 URL을 준다
 */
import { isPharmacyExportEmptyError } from '@/entities/pharmacy/api/pharmacy.api';
import {
  useExportPharmacies,
  usePharmacyList,
} from '@/entities/pharmacy/hooks/usePharmacies';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FacilityCollectionRunButton } from '@/shared/components/FacilityCollectionRunButton';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { pharmacyDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { formatClosedOnDisplay } from '@/shared/utils/facilityClosure';
import {
  getPartnerBusinessStatusBadgeVariant,
  getPartnerBusinessStatusLabel,
  PARTNER_BUSINESS_STATUS,
  toPartnerBusinessStatusFilter,
} from '@/shared/utils/partnerBusinessStatus';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Download, Store } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

/** 화면 라벨은 한글, API 값은 OPEN/SUSPENDED/CLOSED. 전체는 파라미터 생략 */
const BUSINESS_STATUS_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '정상', value: PARTNER_BUSINESS_STATUS.OPEN },
  { label: '휴업', value: PARTNER_BUSINESS_STATUS.SUSPENDED },
  { label: '폐업', value: PARTNER_BUSINESS_STATUS.CLOSED },
] as const;

type BusinessStatusFilter = (typeof BUSINESS_STATUS_OPTIONS)[number]['value'];

/** URL·레거시 한글 값을 라디오 value로 맞춘다 */
const toBusinessStatusUiValue = (value: string): BusinessStatusFilter => {
  const api = toPartnerBusinessStatusFilter(value);
  return api ?? 'all';
};

const PHARMACY_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  pharmacyNameSearchKey: '',
  relatedHospSearchKey: '',
  businessStatus: 'all' as BusinessStatusFilter,
};

export const PharmaciesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(PHARMACY_SEARCH_DEFAULTS);

  const [nameInput, setNameInput] = useState(values.pharmacyNameSearchKey);
  const [relatedHospInput, setRelatedHospInput] = useState(
    values.relatedHospSearchKey,
  );
  const exportMutation = useExportPharmacies();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportMutation.isPending;

  useEffect(() => {
    setNameInput(values.pharmacyNameSearchKey);
    setRelatedHospInput(values.relatedHospSearchKey);
  }, [values.pharmacyNameSearchKey, values.relatedHospSearchKey]);

  useEffect(() => {
    setGlobalLoading(isExporting, '문전약국 목록 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const businessStatusUi = toBusinessStatusUiValue(values.businessStatus);

  const search = useMemo(
    () => ({
      pharmacyNameSearchKey: values.pharmacyNameSearchKey || undefined,
      relatedHospSearchKey: values.relatedHospSearchKey || undefined,
      businessStatusFilter: toPartnerBusinessStatusFilter(
        values.businessStatus,
      ),
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = usePharmacyList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const pharmacies = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasFilters =
    Boolean(values.pharmacyNameSearchKey) ||
    Boolean(values.relatedHospSearchKey) ||
    businessStatusUi !== 'all';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      pharmacyNameSearchKey: nameInput.trim(),
      relatedHospSearchKey: relatedHospInput.trim(),
      page: 1,
    });
  };

  const handleBusinessStatusChange = (value: BusinessStatusFilter) => {
    setValues({
      businessStatus: value,
      page: 1,
    });
  };

  const handleExport = async () => {
    try {
      const archive = await exportMutation.mutateAsync(search);
      if (!archive.downloadUrl) {
        toast.info('다운로드할 문전약국이 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        `문전약국 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isPharmacyExportEmptyError(e)) {
        toast.info('다운로드할 문전약국이 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Store}
        title="문전약국 관리"
        description="문전약국 목록 조회"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || isLoading || totalCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '처리 중...' : '엑셀 다운로드'}
            </Button>
            <FacilityCollectionRunButton />
          </>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label htmlFor="pharmacy-name">약국명</Label>
            <Input
              id="pharmacy-name"
              placeholder="약국명"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="w-48"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="related-hospital">관련 병의원</Label>
            <Input
              id="related-hospital"
              placeholder="관련 병의원"
              value={relatedHospInput}
              onChange={(e) => setRelatedHospInput(e.target.value)}
              className="w-48"
            />
          </div>
          <Button type="submit" className="ruda-button">
            검색
          </Button>
        </FilterRow>

        <div className="space-y-2">
          <Label>영업 상태</Label>
          <RadioGroup
            value={businessStatusUi}
            onValueChange={(v) =>
              handleBusinessStatusChange(v as BusinessStatusFilter)
            }
            className="flex flex-wrap gap-4"
          >
            {BUSINESS_STATUS_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                htmlFor={`pharmacy-business-status-${opt.value}`}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`pharmacy-business-status-${opt.value}`}
                />
                {opt.label}
              </label>
            ))}
          </RadioGroup>
        </div>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="문전약국 목록을 불러오는 중..." />
          </div>
        ) : pharmacies.length === 0 ? (
          <EmptyState
            description={
              hasFilters
                ? '검색 조건에 맞는 문전약국이 없습니다.'
                : '등록된 문전약국이 없습니다.'
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>약국명</TableHead>
                  <TableHead>연락처</TableHead>
                  <TableHead>관련 병의원</TableHead>
                  <TableHead>영업 상태</TableHead>
                  <TableHead>폐업일</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pharmacies.map((pharmacy) => (
                  <TableRow
                    key={pharmacy.pharmacyId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: pharmacyDetailPath(pharmacy.pharmacyId),
                      } as never)
                    }
                  >
                    <TableCell className="font-medium">
                      {pharmacy.pharmacyName}
                    </TableCell>
                    <TableCell>{pharmacy.contactNumber || '-'}</TableCell>
                    <TableCell>{pharmacy.associatedClinics || '-'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge
                        variant={getPartnerBusinessStatusBadgeVariant(
                          pharmacy.businessStatusType,
                        )}
                      >
                        {getPartnerBusinessStatusLabel(
                          pharmacy.businessStatusType,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatClosedOnDisplay(pharmacy.closedOn)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
